import type { MailMessage, MailThread } from "@/lib/api";

function normalizeWs(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/** True when the message has a real body — not a list-row snippet stub. */
export function messageHasRealBody(message: MailMessage | null | undefined): boolean {
  if (!message) return false;
  const html = typeof message.bodyHtml === "string" ? message.bodyHtml.trim() : "";
  if (html) return true;
  const text = typeof message.bodyText === "string" ? message.bodyText.trim() : "";
  if (!text) return false;
  const snippet = typeof message.snippet === "string" ? message.snippet.trim() : "";
  // Synthetic list stub: bodyText was copied from the collapsed snippet.
  if (snippet && normalizeWs(text) === normalizeWs(snippet)) return false;
  return true;
}

/**
 * True when we have enough body content to paint the reader — but a single
 * list/IDB stub must NOT count (that skipped GET /api/mail/:id and left chains
 * mashed). Prefer {@link isMailThreadFullyExpanded} before skipping network.
 */
export function isMailThreadHydrated(thread: MailThread | null | undefined): boolean {
  if (!thread) return false;
  const messages = thread.messages;
  if (!Array.isArray(messages) || messages.length === 0) return false;
  const declared =
    typeof thread.messageCount === "number" && thread.messageCount > 0
      ? thread.messageCount
      : 0;
  // Incomplete expand — more messages exist than we have shells for.
  if (declared > messages.length) return false;
  return messages.some(messageHasRealBody);
}

/**
 * True only after GET /api/mail/:id marked the chain expanded.
 * A folder list can already hold several real bodies and still be missing
 * sent copies that live in another mailbox, so those lists are not "done".
 */
export function isMailThreadFullyExpanded(thread: MailThread | null | undefined): boolean {
  if (!isMailThreadHydrated(thread) || !thread) return false;
  return thread.detailExpanded === true;
}

/**
 * Mark a detail payload as fully expanded and sync messageCount from the
 * messages we actually have. A higher count left over from the folder list
 * used to stick ("Showing 1 of 3") after the reader only held one body.
 * A truncated provider chain keeps the higher count and is not "done".
 */
export function markThreadDetailExpanded(thread: MailThread): MailThread {
  const messages = Array.isArray(thread.messages) ? thread.messages : [];
  const truncated = Boolean(thread.chainTruncated);
  const declared = typeof thread.messageCount === "number" ? thread.messageCount : 0;
  const count = truncated
    ? Math.max(declared, messages.length, 1)
    : Math.max(messages.length, 1);
  const hasAttachments =
    Boolean(thread.hasAttachments) ||
    messages.some((message) => Array.isArray(message.attachments) && message.attachments.length > 0);
  return {
    ...thread,
    messageCount: count,
    hasAttachments,
    detailExpanded: !truncated,
  } as MailThread & { detailExpanded: boolean };
}
